/* ───────────────────────────────────────────────────────────────────────────
   FILE: src/pages/NewTeamMember.jsx
   • Tall 3 : 4 portrait (extra head-room so cover never trims hair)
   • BlazeFace auto-centres on face
   • White 300×200 preview rectangle overlays the cropper (WYSIWYG)
   ───────────────────────────────────────────────────────────────────────── */

import React, { useState, useEffect } from "react";
import "./NewTeamMember.css";

import { db, storage } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";

import Cropper from "react-easy-crop";
import * as blazeface from "@tensorflow-models/blazeface";
import "@tensorflow/tfjs-backend-webgl";

const MASTER_PIN = "LEXTRACKAI50B";

/* ─── Fields shown in the builder ──────────────────────────────────────── */
const FIELD_TYPES = [
  { key: "name",      label: "Full Name",     type: "text",     required: true },
  { key: "position",  label: "Position",      type: "text",     required: true },
  { key: "bio",       label: "Bio",           type: "textarea", required: true },
  { key: "education", label: "Education",     type: "text",     required: true },
  { key: "linkedin",  label: "LinkedIn URL",  type: "text",     required: true },
  { key: "photo",     label: "Headshot file", type: "file",     required: true },
];

/* ─── Utility: crop to Blob ────────────────────────────────────────────── */
async function cropToBlob(url, area) {
  const img = await new Promise(res => { const i=new Image(); i.src=url; i.onload=()=>res(i); });
  const cvs = document.createElement("canvas");
  cvs.width  = area.width;
  cvs.height = area.height;
  cvs.getContext("2d").drawImage(
    img,
    area.x, area.y, area.width, area.height,
    0,     0,        area.width, area.height
  );
  return new Promise(r => cvs.toBlob(b => r(b), "image/jpeg", 0.9));
}

/* ─── Component ────────────────────────────────────────────────────────── */
export default function NewTeamMember({ onClose }) {
  /* PIN gate */
  const [pin, setPin] = useState("");
  const [verified, setVerified] = useState(false);

  /* Form & values */
  const defaultKeys = ["name", "position", "bio", "photo"];
  const [dropped, setDropped] = useState(FIELD_TYPES.filter(f => defaultKeys.includes(f.key)));
  const [vals, setVals] = useState(
    FIELD_TYPES.reduce((o,f)=>{ if(defaultKeys.includes(f.key)) o[f.key]=f.type==="file"?null:""; return o; }, {})
  );

  /* Cropper state */
  const [imgURL, setImgURL] = useState(null);
  const [crop, setCrop]     = useState({ x: 0, y: 0 });
  const [zoom, setZoom]     = useState(1);
  const [cropPx, setCropPx] = useState(null);

  /* Messages */
  const [err,  setErr]  = useState("");
  const [msg,  setMsg]  = useState("");

  /* BlazeFace model */
  const [model, setModel] = useState(null);
  useEffect(() => { blazeface.load().then(setModel); }, []);

  /* Auto-centre on face */
  useEffect(() => {
    if (!imgURL || !model) return;

    (async () => {
      const img = await new Promise(r => { const t=new Image(); t.src=imgURL; t.onload=()=>r(t); });
      const [face] = await model.estimateFaces(img, false) || [];
      if (!face) return;

      const [x0, y0] = face.topLeft;
      const [x1, y1] = face.bottomRight;
      const faceW = x1 - x0;
      const faceH = y1 - y0;

      /* Tall rectangle: head ~30 % of height */
      const H = faceH * 3.4;
      const W = H * 0.75;              // 3:4 aspect

      const cx = x0 + faceW / 2;
      const cy = y0 + faceH / 2 + faceH * 0.30; // bias downward

      const left = Math.max(0, cx - W / 2);
      const top  = Math.max(0, cy - H / 2);

      setCrop({ x: -left, y: -top });
      setZoom(1);
      setCropPx({ x: left, y: top, width: W, height: H });
    })();
  }, [imgURL, model]);

  /* Drag-and-drop toolbox */
  const dragStart = (e,k)=>e.dataTransfer.setData("text/plain",k);
  const dragOver  = e=>e.preventDefault();
  const drop      = e=>{
    e.preventDefault();
    const k=e.dataTransfer.getData("text/plain");
    if(!k||dropped.some(f=>f.key===k)) return;
    const cfg=FIELD_TYPES.find(f=>f.key===k);
    setDropped(p=>[...p,cfg]);
    setVals(p=>({...p,[k]:cfg.type==="file"?null:""}));
  };
  const remove=k=>{
    setDropped(p=>p.filter(f=>f.key!==k));
    setVals(p=>{const n={...p}; delete n[k]; return n;});
    if(k==="photo") setImgURL(null);
  };

  /* Input change */
  const onChange=(e,k,t)=>{
    if(t==="file" && e.target.files[0]){
      setImgURL(URL.createObjectURL(e.target.files[0]));
      setVals(p=>({...p,[k]:e.target.files[0]}));
    }else{
      setVals(p=>({...p,[k]:e.target.value}));
    }
  };

  /* Save */
  const save=async e=>{
    e.preventDefault(); setErr(""); setMsg("");
    if(dropped.some(f=>f.required&&!vals[f.key])) return setErr("Please fill all required fields.");

    try{
      const blob = await cropToBlob(imgURL, cropPx);
      const ref  = storageRef(storage, `avatars/${Date.now()}_portrait.jpg`);
      await uploadBytes(ref, blob);
      const photoUrl = await getDownloadURL(ref);

      const data = { createdAt: serverTimestamp(), photoUrl };
      dropped.forEach(f=>{ if(f.type!=="file") data[f.key]=vals[f.key] || ""; });

      await addDoc(collection(db,"teamMembers"), data);
      setMsg("🎉 Saved! Check the OurCompany page.");
      onClose && onClose();
    }catch(e){console.error(e); setErr("Upload failed.");}
  };

  /* Render */
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e=>e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>Add New Team Member</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {!verified ? (
            <div className="pin-panel">
              <input value={pin} onChange={e=>setPin(e.target.value)} placeholder="Enter team PIN"/>
              <button onClick={()=>setVerified(pin===MASTER_PIN)}>Continue</button>
              {pin && pin!==MASTER_PIN && <p className="np-error-text">Invalid PIN</p>}
            </div>
          ) : (
            <>
              {/* Toolbox */}
              <div className="field-toolbox">
                <h3>Drag Fields Into Form</h3>
                <ul>
                  {FIELD_TYPES.map(f=>(
                    <li key={f.key}>
                      <div
                        className={`toolbox-item ${dropped.some(d=>d.key===f.key)?"disabled":""}`}
                        draggable={!dropped.some(d=>d.key===f.key)}
                        onDragStart={e=>dragStart(e,f.key)}
                      >
                        {f.label}{f.required && <span className="required">*</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Canvas */}
              <div className="form-canvas" onDragOver={dragOver} onDrop={drop}>
                <h3>Form Preview</h3>
                {dropped.length===0 && <p className="canvas-empty">Drag fields here…</p>}

                <form onSubmit={save}>
                  {dropped.map(({key,label,type,required})=>(
                    <div className="form-field" key={key}>
                      <label>{label}{required && <span className="required">*</span>}</label>

                      <div className="field-input-wrapper">
                        {type==="text" && (
                          <input value={vals[key]||""} onChange={e=>onChange(e,key,type)} required={required}/>
                        )}

                        {type==="textarea" && (
                          <textarea value={vals[key]||""} onChange={e=>onChange(e,key,type)} required={required}/>
                        )}

                        {type==="file" && (
                          <>
                            <input type="file" accept="image/*" onChange={e=>onChange(e,key,type)} required={required}/>
                            {imgURL && (
                              <div className="crop-container">
                                <Cropper
                                  image={imgURL}
                                  crop={crop}
                                  zoom={zoom}
                                  aspect={3/4}          /* tall portrait */
                                  showGrid={false}
                                  onCropChange={setCrop}
                                  onZoomChange={setZoom}
                                  onCropComplete={(_,px)=>setCropPx(px)}
                                />

                                {/* ▼ EXACT 300×200 preview rectangle */}
                                <div className="preview-mask" />

                                <input
                                  type="range" min={1} max={3} step={0.01}
                                  value={zoom}
                                  onChange={e=>setZoom(e.target.value)}
                                />
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      <button type="button" className="remove-field-btn" onClick={()=>remove(key)}>Remove</button>
                    </div>
                  ))}

                  {err && <p className="np-error-text">{err}</p>}
                  {msg && <p className="np-success-text">{msg}</p>}

                  <div className="modal-actions">
                    <button type="submit" className="btn-save">Save Profile</button>
                    <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
                  </div>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
